import ReactDom from 'react-dom';
import { Icon as BlueIcon } from '@blueprintjs/core';

import { Dispatch, SetStateAction, useState } from 'react';

import Rolling from '@assets/gif/rolling.gif';

import { useInfiniteQuery, UseMutationResult } from 'react-query';
import { indexManufacturersPagination } from '@services/queries/manufacturers';
import { IManufacturer } from '@models/Manufacturer.model';

import ImagePlaceholderPNG from '@assets/png/manufacturer-placeholder.png';

import { IEquipment, IEquipmentWithoutProduct } from '@models/Equipment.model';
import { ICreateEquipments } from '@services/queries/equipments/createEquipments/createEquipments';
import { IPatchEquipmentsMatch } from '@services/queries/equipments/patchEquipmentsMatch/patchEquipmentsMatch';
import {
  Container,
  Overlay,
  Header,
  Title,
  CloseButton,
  Text,
  Form,
  GroupForm,
  InputWrapper,
  InputLabel,
  InputField,
  Input,
  Actions,
  Button,
  CleanButton,
  IconLeft,
  GlassIcon,
  InputFieldManufacturer,
  FormContainer,
  InputWrapperUrl,
  LoadingSearch,
} from './InternalProductDialog.styles';
import { InputDropDown } from './components/InputDropDown/InputDropDown';
import { QuantityCounter } from '../QuantityCounter/QuantityCounter';

interface IInternalProductDialog {
  open: boolean;
  onClose: () => void;
  productName: string;
  count: number;
  setCount: Dispatch<SetStateAction<number>>;
  startManufacturer: IManufacturer | null;
  startSearchManufacturer: string;
  mutationCreateEquipments: UseMutationResult<
    void | IEquipment[],
    unknown,
    ICreateEquipments,
    unknown
  >;
  mutationMatchEquipment: UseMutationResult<
    IEquipment,
    unknown,
    IPatchEquipmentsMatch,
    unknown
  >;
  showCounter: boolean;
  asset?: IEquipment | IEquipmentWithoutProduct;
}

export const InternalProductDialog = ({
  open,
  onClose,
  productName,
  count,
  setCount,
  startManufacturer,
  startSearchManufacturer,
  mutationCreateEquipments,
  mutationMatchEquipment,
  showCounter,
  asset,
}: IInternalProductDialog) => {
  const portalDiv = document.getElementById('portal');

  const [searchManufacturer, setSearchManufacturer] = useState(
    startManufacturer?.name || startSearchManufacturer,
  );
  const [manufacturer, setManufacturer] = useState<IManufacturer | null>(
    startManufacturer,
  );

  const confirmLoading =
    mutationCreateEquipments.isLoading || mutationMatchEquipment.isLoading;

  const manufacturersQuery = useInfiniteQuery(
    ['manualAdd', 'manufacturers', 'internal', searchManufacturer],
    async ({ pageParam = 1 }) => {
      return indexManufacturersPagination({
        pagination: {
          limit: 20,
          page: pageParam,
          search: searchManufacturer,
        },
      });
    },
    {
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
      enabled: !!searchManufacturer,
    },
  );

  const hasMoreManufacturers = manufacturersQuery.data
    ? manufacturersQuery.data.pages[manufacturersQuery.data.pages.length - 1]
        .hasMore
    : false;

  const manufacturers = manufacturersQuery.data
    ? manufacturersQuery.data.pages
        .map(pageData => pageData.results)
        .reduce(function reduce(prev, next) {
          return prev.concat(next);
        })
    : [];

  const [values, setValues] = useState({
    manufacturer: '',
    product: productName,
    model: '',
    category: '',
    evidenceUrl: '',
  });

  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  const handleInputChange = (e: {
    target: { name: string; value: string };
  }) => {
    const { name, value } = e.target;

    setValues({
      ...values,
      [name]: value,
    });
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>New Internal Product</Title>
              <CloseButton onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </CloseButton>
            </Header>
            <Text>
              Please provide more information to add this product as Internal.
            </Text>

            <FormContainer>
              <Form>
                <GroupForm>
                  <InputWrapper>
                    <InputLabel data-optional-text>Manufacturer</InputLabel>
                    <InputFieldManufacturer
                      data-no-bottom-arc={
                        searchManufacturer.length > 0 && showSearchDropdown
                      }
                    >
                      {!!manufacturer && (
                        <IconLeft
                          src={manufacturer.imageUrl || ImagePlaceholderPNG}
                          brokenSrc={ImagePlaceholderPNG}
                        />
                      )}
                      {!manufacturer && (
                        <GlassIcon>
                          <BlueIcon icon="search" color="#8F95B2" size={12} />
                        </GlassIcon>
                      )}

                      <Input
                        value={searchManufacturer}
                        disabled={confirmLoading}
                        name="manufacturer"
                        onChange={e => {
                          setSearchManufacturer(e.target.value);
                          setShowSearchDropdown(true);
                        }}
                        onKeyDown={() => {
                          setManufacturer(null);
                        }}
                      />

                      {searchManufacturer &&
                        (!manufacturersQuery.isLoading ||
                          !showSearchDropdown) && (
                          <CleanButton
                            onClick={() => {
                              setSearchManufacturer('');
                              setManufacturer(null);
                            }}
                          >
                            <BlueIcon icon="cross" color="#8F95B2" size={12} />
                          </CleanButton>
                        )}
                      {showSearchDropdown && manufacturersQuery.isLoading && (
                        <LoadingSearch src={Rolling} alt="loading" />
                      )}
                    </InputFieldManufacturer>
                    {searchManufacturer && showSearchDropdown && (
                      <InputDropDown
                        onClose={() => setShowSearchDropdown(false)}
                        searchedValue={searchManufacturer}
                        manufacturers={manufacturers}
                        hasMoreManufacturers={hasMoreManufacturers}
                        loadMoreManufacturers={manufacturersQuery.fetchNextPage}
                        isLoading={manufacturersQuery.isLoading}
                        setManufacturer={(newManufacturer: IManufacturer) => {
                          setManufacturer(newManufacturer);
                          setSearchManufacturer(newManufacturer.name);
                          setShowSearchDropdown(false);
                        }}
                      />
                    )}
                  </InputWrapper>
                  <InputWrapper>
                    <InputLabel>Product Name</InputLabel>
                    <InputField>
                      <Input
                        value={values.product}
                        disabled={confirmLoading}
                        name="product"
                        onChange={handleInputChange}
                      />
                    </InputField>
                  </InputWrapper>
                  <InputWrapper>
                    <InputLabel data-optional-text>Model #</InputLabel>
                    <InputField>
                      <Input
                        value={values.model}
                        disabled={confirmLoading}
                        name="model"
                        onChange={handleInputChange}
                      />
                    </InputField>
                  </InputWrapper>
                  {/* <InputWrapper>
                  <InputLabel data-optional-text>Category</InputLabel>
                  <InputField>
                    <Input
                      value={values.category}
                      disabled={confirmLoading}
                      name="category"
                      onChange={handleInputChange}
                    />
                  </InputField>
                </InputWrapper> */}
                </GroupForm>

                <InputWrapperUrl data-top>
                  <InputLabel data-optional-text>Evidence URLs</InputLabel>{' '}
                  <br />
                  <InputLabel data-optional>
                    Any relevant webpage you have found a reference for this
                    Product
                  </InputLabel>
                  <InputField>
                    <Input
                      value={values.evidenceUrl}
                      disabled={confirmLoading}
                      name="evidenceUrl"
                      onChange={handleInputChange}
                    />
                    {values.evidenceUrl && (
                      <CleanButton
                        type="button"
                        disabled={confirmLoading}
                        onClick={() => {
                          setValues({
                            ...values,
                            evidenceUrl: '',
                          });
                        }}
                      >
                        <BlueIcon icon="cross" color="#8F95B2" size={12} />
                      </CleanButton>
                    )}
                  </InputField>
                </InputWrapperUrl>
              </Form>
            </FormContainer>

            <Actions>
              <Button data-neutral onClick={onClose} type="button">
                Cancel
              </Button>
              {showCounter && (
                <QuantityCounter
                  count={count}
                  setCount={setCount}
                  disabled={confirmLoading}
                />
              )}
              <Button
                data-blue
                disabled={!values.product || confirmLoading}
                onClick={() => {
                  if (asset) {
                    mutationMatchEquipment.mutate({
                      equipmentId: asset.id,
                      manufacturerId: manufacturer?.id,
                      manufacturerName: searchManufacturer,
                      productName: values.product,
                      model: values.model,
                      evidenceUrl: values.evidenceUrl,
                    });
                  } else {
                    mutationCreateEquipments.mutate({
                      amountProducts: [
                        {
                          amount: count,
                          manufacturerId: manufacturer?.id,
                          tempManufacturerName: searchManufacturer,
                          tempProductName: values.product,
                          tempProductModel: values.model,
                          resource: values.evidenceUrl,
                        },
                      ],
                    });
                  }
                }}
              >
                {asset ? 'Confirm' : 'Add Asset(s)'}
              </Button>
            </Actions>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
